import { translate } from '@jsverse/transloco';

import { FilterService } from 'app/modules/collection/services/filter.service';
import { Utils } from 'app/utils/utils';
import { AbstractChartReport } from '../chart-report-abstract/chart-report-abstract';
import { ChartDataService } from '../../chart-data.service';

export class FeatureVolumeVersusSentimentChartReport extends AbstractChartReport {
  reportName = 'sentimentVersusVolume';

  getFields() {
    return ['analyticItem', 'selectedItems', 'dateInterval', 'graphType'];
  }

  isAnalyticItemRequired() {
    return true;
  }

  getAggregators() {
    return ['time'];
  }

  isAnalyticItemSupported(analyticItem) {
    return ['metadata'].indexOf(analyticItem) < 0;
  }

  isSelectedItemRequired() {
    return true;
  }

  canOnlyHaveOneSelectedItem() {
    return true;
  }

  canHideItems() {
    return false;
  }

  getGraphTypes() {
    return ['stackedarea', 'stackedbar'];
  }

  canDrilldown() {
    return false;
  }

  public canSort(): boolean {
    return false;
  }

  getSelectedItem() {
    return this.options.selectedItems[0];
  }

  public canSetDateMarkers(): boolean {
    return true;
  }

  public applyComparisonToSeries(_comparison: any, _series: any, _seriesIdx: any): void {
    return;
  }

  getParams() {
    const superParams = super.getParams();
    const featureValue = this.options.selectedItems[0] || '';
    return Object.assign(superParams, { featureValue });
  }

  async processResponse(response, _filterQuery?: string) {
    for (const date of Object.getOwnPropertyNames(response.items)) {
      let total = 0;
      for (const key of Object.getOwnPropertyNames(response.items[date])) {
        total += response.items[date][key];
      }
      for (const key of Object.getOwnPropertyNames(response.items[date])) {
        const value = response.items[date][key];
        if (total) {
          response.items[date][key] = value / total;
        }
      }
      response.items[date].total = total;
    }

    return response;
  }

  reportHandler = (dashboard, widget, response, options) => {
    const data = ChartDataService.forDateHistogram(dashboard, widget, response, options);

    data.yAxis = [
      {
        title: { text: null },
        labels: { format: '{value} %' },
      },
      Object.assign({ opposite: true }, data.yAxis),
    ];

    let graphType = 'column';
    if (this.options.graphType === 'stackedarea') {
      graphType = 'area';
    }

    const { events, shouldBeVisible } = FilterService.createSentimentChartFilterEvents(widget);

    const formatterFactory = () => {
      const chartItem = this.getSelectedItem();
      return function () {
        let tooltipSentimentLabel = translate('widgets.charting.tooltip.sentiment-part', {
          sentiment: this.series.name,
        });
        let tooltipCountLabel = translate('widgets.charting.tooltip.percentage-part', {
          percentage: (this.y * 100).toFixed(0),
        });
        if (this.series.name === 'total') {
          const value = translate('widgets.charting.numberof-documents', {
            count: Utils.addThousandSeparator(this.y),
          });
          tooltipSentimentLabel = '';
          tooltipCountLabel = translate('widgets.charting.tooltip.count-part', { count: value });
        }
        return `${translate('widgets.charting.tooltip.name-part', {
          name: chartItem,
        })} <span style="color:${this.color}">\u25CF</span><br/>
          ${tooltipSentimentLabel}<br/>
          ${tooltipCountLabel}`;
      };
    };

    const result = {
      tooltip: {
        formatter: formatterFactory(),
      },
      chart: {
        zoomType: dashboard?.locked ? false : 'x',
        type: graphType,
        alignTicks: false,
      },
      xAxis: {
        type: 'datetime',
        events: {
          afterSetExtremes: ChartDataService.setZoom.bind(null, widget, dashboard.payload.timezone),
        },
        labels: ChartDataService.getDateLabelsOptions(widget),
      },
      yAxis: [
        {
          title: {
            text: translate('widgets.charting.sentiment-percentage'),
          },
          gridLineWidth: 0,
        },
        {
          title: {
            text: translate('widgets.charting.documents'),
          },
          opposite: true,
        },
      ],
      plotOptions: { series: { stacking: 'percent', events, turboThreshold: 0 } },
      series: [],
    };

    if (
      widget.payload.zoomDateFilter &&
      widget.payload.filters.dates.and.findIndex(
        (filter) => filter.value === widget.payload.zoomDateFilter
      ) !== -1
    ) {
      (result as any).exporting = {
        buttons: {
          contextButton: {
            enabled: false,
          },
        },
      };
      (result as any).navigation = {
        buttonOptions: {
          theme: {
            'stroke-width': 2,
            stroke: '#1274d1',
          },
        },
      };
    }

    if (data.series.length > 0) {
      const getDataWithDocumentCount = (dataArray: Array<any>, totalData: Array<any>) => {
        return dataArray.map((d) => ({
          ...d,
          documentCount: Math.floor(totalData.find((t) => t.x === d.x).y * d.y),
        }));
      };
      switch (dashboard.payload.sentimentScale.toString()) {
        default:
        case '':
        case '3':
          result.series = [
            data.series.find((s) => s.name === 'positive'),
            data.series.find((s) => s.name === 'neutral'),
            data.series.find((s) => s.name === 'negative'),
            data.series.find((s) => s.name === 'total'),
          ];

          result.series[0].color = '#55BF3B';
          result.series[0].visible = shouldBeVisible('positive');
          result.series[1].color = '#cccccc';
          result.series[1].visible = shouldBeVisible('neutral');
          result.series[2].color = '#DF5353';
          result.series[2].visible = shouldBeVisible('negative');
          result.series[3].color = '#000000';
          result.series[3].visible = shouldBeVisible('total', true);
          result.series[3].stacking = null;
          result.series[3].type = 'line';
          result.series[3].yAxis = 1;
          result.series.forEach((s, idx) => {
            if (idx < result.series.length - 1) {
              s.data = getDataWithDocumentCount(
                s.data,
                result.series[result.series.length - 1].data
              );
            }
          });
          break;

        case '5':
          result.series = [
            data.series.find((s) => s.name === 'very positive'),
            data.series.find((s) => s.name === 'somewhat positive'),
            data.series.find((s) => s.name === 'neutral'),
            data.series.find((s) => s.name === 'somewhat negative'),
            data.series.find((s) => s.name === 'very negative'),
            data.series.find((s) => s.name === 'total'),
          ];

          result.series[0].color = '#55BF3B';
          result.series[0].visible = shouldBeVisible('very positive');
          result.series[1].color = '#BFFFB2';
          result.series[1].visible = shouldBeVisible('somewhat positive');
          result.series[2].color = '#cccccc';
          result.series[2].visible = shouldBeVisible('neutral');
          result.series[3].color = '#FE9999';
          result.series[3].visible = shouldBeVisible('somewhat negative');
          result.series[4].color = '#DF5353';
          result.series[4].visible = shouldBeVisible('very negative');
          result.series[5].color = '#000000';
          result.series[5].visible = shouldBeVisible('total', true);
          result.series[5].stacking = null;
          result.series[5].type = 'line';
          result.series[5].yAxis = 1;
          result.series.forEach((s, idx) => {
            if (idx < result.series.length - 1) {
              s.data = getDataWithDocumentCount(
                s.data,
                result.series[result.series.length - 1].data
              );
            }
          });
          break;
      }

      result.series.forEach((s) =>
        s.data.forEach((d) => {
          d.id = `${d.x}-${d.pointId}`;
        })
      );
    }

    result['extra'] = response['extra'] ? response['extra'] : {};
    result['reportName'] = response['reportName'];
    return result;
  };
}

export class MetadataFeatureVolumeVersusSentimentChartReport extends FeatureVolumeVersusSentimentChartReport {
  reportName = 'metadataSentimentVersusVolume';

  getSelectedItem(): string {
    return this.formatMetadataValueFactory(this.options.selectedItems[0]);
  }
}

export class TaxonomyVolumeVersusSentimentChartReport extends FeatureVolumeVersusSentimentChartReport {
  reportName = 'taxonomySentimentVersusVolume';

  taxonomyNodes = {};

  getSelectedItem(): string {
    return this.taxonomyNodes[this.options.selectedItems[0]]?.name;
  }

  async processResponse(response, filterQuery?: string) {
    this.taxonomyNodes = response.extra['taxonomyNodes'] ?? {};

    return super.processResponse(response, filterQuery);
  }
}
